import { useState } from 'react';

export default function Home() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    
    setFormStatus('submitting');
    try {
      const form = e.target as HTMLFormElement;
      const data = new URLSearchParams();
      data.append('name', formData.name);
      data.append('email', formData.email);
      data.append('message', formData.message);
      
      await fetch(form.action, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: data.toString()
      });
      
      setFormStatus('success');
      setFormData({ name: '', email: '', message: '' });
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <a href="#home" className="text-2xl font-bold text-gray-900" style={{ fontFamily: '"Pacifico", serif' }}>
                logo
              </a>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">Home</a>
              <a href="#features" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">Features</a>
              <a href="#about" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">About</a>
              <a href="#contact" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">Contact</a>
              <a href="#download" className="bg-emerald-600 text-white px-6 py-2 rounded-full hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap">
                Download
              </a>
            </div>

            <button 
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
            <div className="px-4 py-4 space-y-3">
              <a href="#home" className="block text-gray-700 hover:text-emerald-600 cursor-pointer">Home</a>
              <a href="#features" className="block text-gray-700 hover:text-emerald-600 cursor-pointer">Features</a>
              <a href="#about" className="block text-gray-700 hover:text-emerald-600 cursor-pointer">About</a>
              <a href="#contact" className="block text-gray-700 hover:text-emerald-600 cursor-pointer">Contact</a>
              <a href="#download" className="block bg-emerald-600 text-white px-6 py-2 rounded-full text-center cursor-pointer whitespace-nowrap">
                Download
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center pt-16">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/c612050a097319cdc9cb188b0c79d2b0.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-transparent"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Secure Your Digital Life with <span className="text-emerald-600">ZITO VPN</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Experience lightning-fast, military-grade encrypted VPN protection. Browse anonymously, access global content, and keep your data safe from prying eyes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="#download" 
                className="bg-emerald-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap text-center"
              >
                Get Started Free
              </a>
              <a 
                href="#features" 
                className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-full text-lg font-semibold hover:border-emerald-600 hover:text-emerald-600 transition-colors cursor-pointer whitespace-nowrap text-center"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose ZITO VPN?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our VPN solution provides comprehensive protection with cutting-edge technology
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-emerald-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Military-Grade Encryption</h3>
              <p className="text-gray-600">
                AES-256 encryption protects your data with the same security used by governments worldwide.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-global-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Global Server Network</h3>
              <p className="text-gray-600">
                Connect to servers in 50+ countries for unrestricted access to content worldwide.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-speed-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Lightning Fast Speeds</h3>
              <p className="text-gray-600">
                Optimized servers ensure minimal speed loss while maintaining maximum security.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-eye-off-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">No-Log Policy</h3>
              <p className="text-gray-600">
                We never track, collect, or share your browsing data. Your privacy is guaranteed.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-device-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Multi-Device Support</h3>
              <p className="text-gray-600">
                Protect all your devices with one account. Available on Windows, Mac, iOS, and Android.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-customer-service-2-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">24/7 Support</h3>
              <p className="text-gray-600">
                Our dedicated support team is always ready to help you with any questions or issues.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How ZITO VPN Works</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get protected in three simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Download & Install</h3>
              <p className="text-gray-600">
                Download ZITO VPN for your device and complete the quick installation process.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Choose a Server</h3>
              <p className="text-gray-600">
                Select from our global network of servers or let us choose the fastest one for you.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Browse Securely</h3>
              <p className="text-gray-600">
                Enjoy private, secure, and unrestricted internet access anywhere in the world.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* App Preview Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Intuitive Design, Powerful Protection
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Our user-friendly interface makes it easy to connect and stay protected. One tap is all it takes to secure your connection.
              </p>
              <ul className="space-y-4">
                <li className="flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center mr-3">
                    <i className="ri-check-line text-emerald-600 text-xl"></i>
                  </div>
                  <span className="text-gray-700">One-click connection</span>
                </li>
                <li className="flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center mr-3">
                    <i className="ri-check-line text-emerald-600 text-xl"></i>
                  </div>
                  <span className="text-gray-700">Smart server selection</span>
                </li>
                <li className="flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center mr-3">
                    <i className="ri-check-line text-emerald-600 text-xl"></i>
                  </div>
                  <span className="text-gray-700">Real-time connection status</span>
                </li>
                <li className="flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center mr-3">
                    <i className="ri-check-line text-emerald-600 text-xl"></i>
                  </div>
                  <span className="text-gray-700">Bandwidth monitoring</span>
                </li>
              </ul>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20VPN%20mobile%20application%20interface%20mockup%20showing%20connection%20screen%20with%20world%20map%20and%20server%20locations%20clean%20minimal%20design%20dark%20theme%20with%20green%20accent%20colors%20floating%20smartphone%20device&width=600&height=700&seq=app1&orientation=portrait"
                alt="ZITO VPN App Interface"
                className="rounded-2xl shadow-2xl w-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20modern%20office%20workspace%20with%20team%20of%20developers%20working%20on%20cybersecurity%20software%20multiple%20monitors%20showing%20code%20and%20security%20dashboards%20bright%20contemporary%20tech%20company%20environment&width=600&height=500&seq=about1&orientation=landscape"
                alt="ZITO-MEDIA Team"
                className="rounded-2xl shadow-lg w-full object-cover object-top"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">About ZITO-MEDIA</h2>
              <p className="text-lg text-gray-600 mb-6">
                ZITO-MEDIA D.O.O. KULA is a Serbian software development company dedicated to creating secure, user-friendly applications that protect your digital privacy.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our mission is to make online privacy accessible to everyone. We believe that internet freedom and security should not be a luxury, but a fundamental right for all users.
              </p>
              <p className="text-lg text-gray-600">
                Based in Kula, Serbia, our team of experienced developers and security experts work tirelessly to deliver cutting-edge VPN technology that keeps you safe online.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="py-24 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Download ZITO VPN Today</h2>
          <p className="text-xl text-emerald-100 mb-12 max-w-2xl mx-auto">
            Available on all major platforms. Start protecting your privacy now.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="#" 
              className="bg-white text-gray-900 px-8 py-4 rounded-xl flex items-center space-x-3 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-apple-fill text-2xl text-gray-900"></i>
              <div className="text-left">
                <div className="text-xs text-gray-500">Download for</div>
                <div className="font-semibold">macOS</div>
              </div>
            </a>
            
            <a 
              href="#" 
              className="bg-white text-gray-900 px-8 py-4 rounded-xl flex items-center space-x-3 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-apple-fill text-2xl text-gray-900"></i>
              <div className="text-left">
                <div className="text-xs text-gray-500">Download for</div>
                <div className="font-semibold">iOS</div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Get In Touch</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Have questions? We&apos;d love to hear from you. Send us a message and we&apos;ll respond as soon as possible.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-emerald-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Address</h4>
                    <p className="text-gray-600">
                      Josipa Kramera b.b.<br />
                      Kula, PAK 436027<br />
                      Serbia
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-mail-line text-xl text-emerald-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                    <a href="mailto:support@zito-mediadookula.com" className="text-emerald-600 hover:text-emerald-700 cursor-pointer">
                      support@zito-mediadookula.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-phone-line text-xl text-emerald-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                    <a href="tel:+19522959449" className="text-emerald-600 hover:text-emerald-700 cursor-pointer">
                      +1 952 295 9449
                    </a>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="mt-8">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d11264.876543210987!2d19.5234567!3d45.6123456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x475b5a1234567890%3A0x1234567890abcdef!2sKula%2C%20Serbia!5e0!3m2!1sen!2s!4v1234567890123"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-xl"
                  title="ZITO-MEDIA Location"
                ></iframe>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-2xl shadow-sm">
              <form 
                id="contact-form"
                data-readdy-form
                action="https://readdy.ai/api/form/d4g4mo5fv7b2bv2ngi9g"
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors text-sm"
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors text-sm"
                    placeholder="john@example.com"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value.slice(0, 500) })}
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors resize-none text-sm"
                    placeholder="How can we help you?"
                    required
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-emerald-600 text-white py-4 rounded-xl font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>

                {formStatus === 'success' && (
                  <p className="text-emerald-600 text-center">Message sent successfully!</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-red-600 text-center">Failed to send message. Please try again.</p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="text-2xl font-bold mb-4" style={{ fontFamily: '"Pacifico", serif' }}>
                logo
              </div>
              <p className="text-gray-400 mb-4 max-w-md">
                ZITO-MEDIA D.O.O. KULA provides secure VPN solutions to protect your online privacy and freedom.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill"></i>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#home" className="text-gray-400 hover:text-emerald-500 transition-colors cursor-pointer">Home</a></li>
                <li><a href="#features" className="text-gray-400 hover:text-emerald-500 transition-colors cursor-pointer">Features</a></li>
                <li><a href="#about" className="text-gray-400 hover:text-emerald-500 transition-colors cursor-pointer">About Us</a></li>
                <li><a href="#download" className="text-gray-400 hover:text-emerald-500 transition-colors cursor-pointer">Download</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-emerald-500 transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-emerald-500 transition-colors cursor-pointer">Privacy Policy</a></li>
                <li><a href="#" className="text-gray-400 hover:text-emerald-500 transition-colors cursor-pointer">Terms of Service</a></li>
                <li><a href="#" className="text-gray-400 hover:text-emerald-500 transition-colors cursor-pointer">Cookie Policy</a></li>
                <li><a href="#" className="text-gray-400 hover:text-emerald-500 transition-colors cursor-pointer">GDPR</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              © {new Date().getFullYear()} ZITO-MEDIA D.O.O. KULA. All rights reserved.
            </p>
            <a 
              href="https://readdy.ai/?origin=logo" 
              rel="nofollow" 
              className="text-gray-400 hover:text-emerald-500 text-sm transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
